import {
  CalendarOutlined,
  CloseOutlined,
  DeleteOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  Upload,
} from 'antd';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  parse,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { DonorStatus, UserRole, useGetUsersListLazyQuery } from 'gql/types';
import { readFileAsResizedBase64 } from 'modules/dashboard/utils/avatarUtils';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SAVE_APP_SETTINGS_MUTATION } from '../../graphql/mutations';
import { GET_APP_SETTINGS_QUERY } from '../../graphql/queries';

const { Title, Text } = Typography;

interface IMultiDateCalendarProps {
  selectedDates: Date[];
  onDateToggle: (date: Date) => void;
}

// Use a leap year (2024) as base to ensure Feb 29 is always available
const LEAP_YEAR = 2024;

const MultiDateCalendar: FC<IMultiDateCalendarProps> = ({
  selectedDates,
  onDateToggle,
}) => {
  const [currentMonth, setCurrentMonth] = useState(0); // 0-11 for Jan-Dec

  const calendarDays = useMemo(() => {
    // Use leap year to ensure Feb has 29 days
    const monthDate = new Date(LEAP_YEAR, currentMonth, 1);
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  // Compare by month and day only (ignoring year) for recurring dates
  const isSelected = (date: Date) =>
    selectedDates.some(
      (d) => d.getMonth() === date.getMonth() && d.getDate() === date.getDate()
    );

  return (
    <div style={{ width: 252 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 4px',
        }}
      >
        <Button
          type="text"
          size="small"
          icon={<LeftOutlined />}
          onClick={() =>
            setCurrentMonth((prev) => (prev === 0 ? 11 : prev - 1))
          }
        />
        <Text strong>
          {format(new Date(LEAP_YEAR, currentMonth, 1), 'MMMM')}
        </Text>
        <Button
          type="text"
          size="small"
          icon={<RightOutlined />}
          onClick={() =>
            setCurrentMonth((prev) => (prev === 11 ? 0 : prev + 1))
          }
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 2,
          textAlign: 'center',
        }}
      >
        {calendarDays.map((day) => {
          const selected = isSelected(day);
          const inCurrentMonth = day.getMonth() === currentMonth;

          return (
            <div
              key={day.toISOString()}
              onClick={() => onDateToggle(day)}
              style={{
                padding: 4,
                cursor: 'pointer',
                opacity: inCurrentMonth ? 1 : 0.3,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  lineHeight: '28px',
                  borderRadius: '50%',
                  backgroundColor: selected ? '#ff4d4f' : undefined,
                  color: selected ? '#fff' : undefined,
                  margin: 'auto',
                  transition: 'all 0.2s',
                }}
              >
                {format(day, 'd')}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ISO 8601: 1=Monday, 2=Tuesday, ..., 7=Sunday
const WEEKDAYS_ISO = [
  { day: 'Mo', isoDay: 1 },
  { day: 'Tu', isoDay: 2 },
  { day: 'We', isoDay: 3 },
  { day: 'Th', isoDay: 4 },
  { day: 'Fr', isoDay: 5 },
  { day: 'Sa', isoDay: 6 },
  { day: 'Su', isoDay: 7 },
];

const STATUS_COLORS = {
  offtrack: '#f31a25',
  atRisk: '#faad14',
  onTrack: '#389e0d',
};

// Role restriction settings per role
interface IRoleRestriction {
  lockToRegion: boolean;
  lockToOffice: boolean;
  hideDashboard: boolean;
  hideRollingDates: boolean;
  hideCalendarDates: boolean;
  showLocationRecruitStats: boolean;
}

// Settings interface for JSON structure
interface IRecruiterDashboardSettingsJson {
  showDebug: boolean;
  excludedDates: string[]; // Store as ISO strings for JSON serialization
  workingDays: number[];
  dayPrbcGoal: number | null;
  statusBreakpoints: [number, number];
  statusLabels: {
    low: string;
    mid: string;
    high: string;
  };
  roleRestrictionsEnabled: boolean;
  roleRestrictions: Partial<Record<UserRole, IRoleRestriction>>;
  activeDonorStatuses: DonorStatus[]; // Statuses considered "active" for pipeline health
  pendingDonorStatuses: DonorStatus[]; // Statuses considered "pending" for pipeline health
  approvedDonorStatuses: DonorStatus[]; // Statuses considered "approved" for pipeline health
  repeatDonationsThreshold: number; // Min donations to be considered a repeat donor
  churnRateBreakpoints: [number, number];
  churnRateLabels: {
    low: string;
    mid: string;
    high: string;
  };
  pendingDaysBreakpoints: [number, number];
  pendingDaysLabels: {
    low: string;
    mid: string;
    high: string;
  };
  donorDonationFrequency: number;
  donorShowRateBreakpoints: [number, number];
  donorShowRateLabels: {
    low: string;
    mid: string;
    high: string;
  };
  lapsedDonorBreakpoints: number[];
  lapsedDonorLabels: string[];
  retiredDonorBreakpoints: number[];
  retiredDonorLabels: string[];
  historyDataCutoff: string; // YYYY-MM-DD
  activeLocationThresholdDays: number; // Location is "active" if ≥1 donation within this many days
  donorEligibilityDays: number; // Days after registration before donor is eligible to donate
  ffpSglProductIds: number[]; // WooCommerce product IDs for FFP SGL (multiplier = 1)
  ffpDblProductIds: number[]; // WooCommerce product IDs for FFP DBL (multiplier = 2)
  avatars: Record<string, string>; // userId → base64 data URI
  defaultAvatarBase64: string | null; // Default avatar base64 data URI
  simulateRole: UserRole | null; // Admin-only: simulate viewing as this role
  simulateUserId: string | null; // Admin-only: simulate as a specific user
}

// All available user roles for the settings UI
const ALL_USER_ROLES: UserRole[] = Object.values(UserRole);

// All available donor statuses for the settings UI
const ALL_DONOR_STATUSES: DonorStatus[] = Object.values(DonorStatus);

// Default role restriction (no locks)
const DEFAULT_ROLE_RESTRICTION: IRoleRestriction = {
  lockToRegion: false,
  lockToOffice: false,
  hideDashboard: false,
  hideRollingDates: false,
  hideCalendarDates: false,
  showLocationRecruitStats: false,
};

// Default settings values
const DEFAULT_SETTINGS: IRecruiterDashboardSettingsJson = {
  showDebug: false,
  excludedDates: [],
  workingDays: [1, 2, 3, 4, 5],
  dayPrbcGoal: 25,
  statusBreakpoints: [50, 75],
  statusLabels: {
    low: 'Offtrack',
    mid: 'At Risk',
    high: 'On Track',
  },
  roleRestrictionsEnabled: false,
  roleRestrictions: {},
  activeDonorStatuses: [
    DonorStatus.Active,
    DonorStatus.Approved,
    DonorStatus.AnnualLabworkDue,
  ],
  pendingDonorStatuses: [DonorStatus.Pending],
  approvedDonorStatuses: [DonorStatus.Approved],
  repeatDonationsThreshold: 4,
  churnRateBreakpoints: [5, 10],
  churnRateLabels: {
    low: 'Good',
    mid: 'OK',
    high: 'Poor',
  },
  pendingDaysBreakpoints: [30, 45],
  pendingDaysLabels: {
    low: 'On Track',
    mid: 'Approaching',
    high: 'Overdue',
  },
  donorDonationFrequency: 56,
  donorShowRateBreakpoints: [50, 75],
  donorShowRateLabels: {
    low: 'Poor',
    mid: 'OK',
    high: 'Good',
  },
  lapsedDonorBreakpoints: [30, 60, 90],
  lapsedDonorLabels: ['Recent', 'Moderate', 'Extended', 'Critical'],
  retiredDonorBreakpoints: [30, 60, 90],
  retiredDonorLabels: ['Recent', 'Moderate', 'Extended', 'Critical'],
  historyDataCutoff: '2025-08-01',
  activeLocationThresholdDays: 90,
  donorEligibilityDays: 40,
  ffpSglProductIds: [502, 8179],
  ffpDblProductIds: [503, 504],
  avatars: {},
  defaultAvatarBase64: null,
  simulateRole: null,
  simulateUserId: null,
};

const SETTINGS_TAB = 'recruiter-dashboard';

// ── Avatar Management Section ──────────────────────────────────────

interface IAvatarManagementSectionProps {
  avatars: Record<string, string>;
  setAvatars: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  avatarNames: Record<string, string>;
  setAvatarNames: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  defaultAvatarBase64: string | null;
  setDefaultAvatarBase64: React.Dispatch<React.SetStateAction<string | null>>;
  markModified: () => void;
}

const AvatarManagementSection: FC<IAvatarManagementSectionProps> = ({
  avatars,
  setAvatars,
  avatarNames,
  setAvatarNames,
  defaultAvatarBase64,
  setDefaultAvatarBase64,
  markModified,
}) => {
  const [addingAvatar, setAddingAvatar] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserLabel, setSelectedUserLabel] = useState<string>('');
  const [userSearchValue, setUserSearchValue] = useState('');

  const [fetchUsers, { data: usersData, loading: usersLoading }] =
    useGetUsersListLazyQuery();

  const handleUserSearch = useCallback(
    (value: string) => {
      setUserSearchValue(value);
      if (value.length >= 2) {
        fetchUsers({
          variables: {
            input: {
              filter: { searchingQuery: value, isArchived: false },
              take: 20,
              skip: 0,
            },
          },
        });
      }
    },
    [fetchUsers]
  );

  const userOptions = useMemo(() => {
    if (!usersData?.getUsersList?.data) return [];
    return usersData.getUsersList.data.map(
      (u: {
        id: string;
        firstName: string;
        lastName: string;
        email?: string;
      }) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}${u.email ? ` (${u.email})` : ''}`,
      })
    );
  }, [usersData]);

  const handleDefaultImageUpload = async (file: File) => {
    try {
      const base64 = await readFileAsResizedBase64(file, 150);
      setDefaultAvatarBase64(base64);
      markModified();
      message.success('Default avatar set');
    } catch {
      message.error('Failed to read image');
    }
    return false; // prevent auto upload
  };

  const handleAvatarUpload = async (file: File) => {
    if (!selectedUserId) {
      message.error('Please select a user first');
      return false;
    }
    try {
      const base64 = await readFileAsResizedBase64(file, 150);
      setAvatars((prev) => ({ ...prev, [selectedUserId]: base64 }));
      setAvatarNames((prev) => ({
        ...prev,
        [selectedUserId]: selectedUserLabel,
      }));
      markModified();
      message.success(`Avatar set for ${selectedUserLabel}`);
      setAddingAvatar(false);
      setSelectedUserId(null);
      setSelectedUserLabel('');
    } catch {
      message.error('Failed to read image');
    }
    return false; // prevent auto upload
  };

  const handleRemoveAvatar = (userId: string) => {
    setAvatars((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    setAvatarNames((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    markModified();
  };

  const avatarEntries = Object.entries(avatars);

  return (
    <Card>
      <Title level={5}>Leaderboard Avatars</Title>
      <Text type="secondary">
        Manage avatar images for the Recruiters Leaderboard card. Avatars are
        stored as compressed images in settings.
      </Text>

      {/* Default Image */}
      <div style={{ marginTop: 20 }}>
        <Text strong>Default Image</Text>
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: 12, marginBottom: 8 }}
        >
          Shown for users without a custom avatar (positions 4+)
        </Text>
        <Space align="start">
          {defaultAvatarBase64 ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={defaultAvatarBase64}
                alt="Default avatar"
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 8,
                  objectFit: 'cover',
                  border: '1px solid #d9d9d9',
                }}
              />
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => {
                  setDefaultAvatarBase64(null);
                  markModified();
                }}
                style={{ position: 'absolute', top: -8, right: -8 }}
              />
            </div>
          ) : (
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleDefaultImageUpload}
            >
              <Button icon={<PlusOutlined />}>Upload Default Image</Button>
            </Upload>
          )}
        </Space>
      </div>

      {/* User Avatars */}
      <div style={{ marginTop: 24 }}>
        <Row justify="space-between" align="middle">
          <Text strong>User Avatars</Text>
          {!addingAvatar && (
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setAddingAvatar(true)}
            >
              Add Avatar
            </Button>
          )}
        </Row>

        {/* Add Avatar form */}
        {addingAvatar && (
          <Card size="small" style={{ marginTop: 8, background: '#fafafa' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                1. Select user, 2. Upload image
              </Text>
              <Select
                showSearch
                placeholder="Search for a user..."
                filterOption={false}
                onSearch={handleUserSearch}
                onChange={(value: string, option: unknown) => {
                  setSelectedUserId(value);
                  setSelectedUserLabel(
                    (option as { label: string })?.label || ''
                  );
                }}
                value={selectedUserId}
                loading={usersLoading}
                options={userOptions}
                style={{ width: '100%' }}
                notFoundContent={
                  userSearchValue.length < 2
                    ? 'Type 2+ characters to search'
                    : usersLoading
                    ? 'Searching...'
                    : 'No users found'
                }
              />
              {selectedUserId && (
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleAvatarUpload}
                >
                  <Button icon={<PlusOutlined />} type="primary" size="small">
                    Upload Avatar for {selectedUserLabel}
                  </Button>
                </Upload>
              )}
              <Button
                type="text"
                size="small"
                onClick={() => {
                  setAddingAvatar(false);
                  setSelectedUserId(null);
                  setSelectedUserLabel('');
                }}
              >
                Cancel
              </Button>
            </Space>
          </Card>
        )}

        {/* Avatar list */}
        {avatarEntries.length === 0 ? (
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: 8, fontSize: 12 }}
          >
            No custom avatars assigned yet.
          </Text>
        ) : (
          <div style={{ marginTop: 8 }}>
            {avatarEntries.map(([userId, base64]) => (
              <div
                key={userId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '6px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <img
                  src={base64}
                  alt="avatar"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
                <Text style={{ flex: 1, fontSize: 13 }}>
                  {avatarNames[userId] || userId.substring(0, 8) + '...'}
                </Text>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveAvatar(userId)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

const RecruiterDashboardSettings: FC = () => {
  const { t } = useTranslation('settings.RecruiterDashboardSettings');
  const [showDebug, setShowDebug] = useState(DEFAULT_SETTINGS.showDebug);
  const [excludedDates, setExcludedDates] = useState<Date[]>([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dayPrbcGoal, setDayPrbcGoal] = useState<number | null>(25);
  // ISO 8601: Mon-Fri selected (1-5), Sat-Sun not selected (6, 7)
  const [workingDays, setWorkingDays] = useState<number[]>([1, 2, 3, 4, 5]);
  // Status breakpoints: [offtrack max, at risk max] - on track is everything above
  const [statusBreakpoints, setStatusBreakpoints] = useState<[number, number]>([
    50, 75,
  ]);
  // Custom status labels
  const [statusLabels, setStatusLabels] = useState({
    low: t('offtrackLabel'),
    mid: t('atRiskLabel'),
    high: t('onTrackLabel'),
  });
  // Role-based filter restrictions
  const [roleRestrictionsEnabled, setRoleRestrictionsEnabled] = useState(false);
  const [roleRestrictions, setRoleRestrictions] = useState<
    Partial<Record<UserRole, IRoleRestriction>>
  >({});
  // Active donor statuses for pipeline health
  const [activeDonorStatuses, setActiveDonorStatuses] = useState<DonorStatus[]>(
    DEFAULT_SETTINGS.activeDonorStatuses
  );
  const [pendingDonorStatuses, setPendingDonorStatuses] = useState<
    DonorStatus[]
  >(DEFAULT_SETTINGS.pendingDonorStatuses);
  const [approvedDonorStatuses, setApprovedDonorStatuses] = useState<
    DonorStatus[]
  >(DEFAULT_SETTINGS.approvedDonorStatuses);
  // Repeat donations threshold
  const [repeatDonationsThreshold, setRepeatDonationsThreshold] =
    useState<number>(DEFAULT_SETTINGS.repeatDonationsThreshold);
  // Churn rate breakpoints and labels
  const [churnRateBreakpoints, setChurnRateBreakpoints] = useState<
    [number, number]
  >(DEFAULT_SETTINGS.churnRateBreakpoints);
  const [churnRateLabels, setChurnRateLabels] = useState(
    DEFAULT_SETTINGS.churnRateLabels
  );
  // Pending days breakpoints and labels
  const [pendingDaysBreakpoints, setPendingDaysBreakpoints] = useState<
    [number, number]
  >(DEFAULT_SETTINGS.pendingDaysBreakpoints);
  const [pendingDaysLabels, setPendingDaysLabels] = useState(
    DEFAULT_SETTINGS.pendingDaysLabels
  );
  // Donor donation frequency and show rate labels
  const [donorDonationFrequency, setDonorDonationFrequency] = useState<number>(
    DEFAULT_SETTINGS.donorDonationFrequency
  );
  const [donorShowRateBreakpoints, setDonorShowRateBreakpoints] = useState<
    [number, number]
  >(DEFAULT_SETTINGS.donorShowRateBreakpoints);
  const [donorShowRateLabels, setDonorShowRateLabels] = useState(
    DEFAULT_SETTINGS.donorShowRateLabels
  );
  // Lapsed donor breakpoints and labels
  const [lapsedDonorBreakpoints, setLapsedDonorBreakpoints] = useState<
    number[]
  >(DEFAULT_SETTINGS.lapsedDonorBreakpoints);
  const [lapsedDonorLabels, setLapsedDonorLabels] = useState<string[]>(
    DEFAULT_SETTINGS.lapsedDonorLabels
  );
  // Retired donor breakpoints and labels
  const [retiredDonorBreakpoints, setRetiredDonorBreakpoints] = useState<
    number[]
  >(DEFAULT_SETTINGS.retiredDonorBreakpoints);
  const [retiredDonorLabels, setRetiredDonorLabels] = useState<string[]>(
    DEFAULT_SETTINGS.retiredDonorLabels
  );
  const [historyDataCutoff, setHistoryDataCutoff] = useState<string>(
    DEFAULT_SETTINGS.historyDataCutoff
  );
  const [activeLocationThresholdDays, setActiveLocationThresholdDays] =
    useState<number>(DEFAULT_SETTINGS.activeLocationThresholdDays);
  const [donorEligibilityDays, setDonorEligibilityDays] = useState<number>(
    DEFAULT_SETTINGS.donorEligibilityDays
  );
  const [ffpSglProductIdsStr, setFfpSglProductIdsStr] = useState<string>(
    DEFAULT_SETTINGS.ffpSglProductIds.join(',')
  );
  const [ffpDblProductIdsStr, setFfpDblProductIdsStr] = useState<string>(
    DEFAULT_SETTINGS.ffpDblProductIds.join(',')
  );
  const [avatars, setAvatars] = useState<Record<string, string>>(
    DEFAULT_SETTINGS.avatars
  );
  const [avatarNames, setAvatarNames] = useState<Record<string, string>>({});
  const [defaultAvatarBase64, setDefaultAvatarBase64] = useState<string | null>(
    DEFAULT_SETTINGS.defaultAvatarBase64
  );
  // Admin simulate user role
  const [simulateRole, setSimulateRole] = useState<UserRole | null>(null);
  const [simulateUserId, setSimulateUserId] = useState<string | null>(null);

  // Fetch users by selected simulation role
  const [fetchSimulateUsers, simulateUsersQuery] = useGetUsersListLazyQuery();

  useEffect(() => {
    if (simulateRole && simulateRole !== UserRole.Admin) {
      fetchSimulateUsers({
        variables: {
          input: {
            filter: { roles: [simulateRole], isArchived: false },
            take: 200,
          },
        },
      });
    }
  }, [simulateRole, fetchSimulateUsers]);

  const simulateUserOptions = useMemo(() => {
    if (!simulateUsersQuery.data?.getUsersList?.data) return [];
    return simulateUsersQuery.data.getUsersList.data
      .map((user) => {
        const name = `${user.firstName} ${user.lastName}`;
        const regionText = user.productRegionPreference?.name || 'No region';
        const officeText = user.assignedOffice?.name || 'No office';
        return {
          value: user.id,
          searchLabel: `${name} ${regionText} ${officeText}`,
          label: (
            <span>
              {name} <Text type="secondary">({regionText})</Text>{' '}
              <Text type="secondary" style={{ fontSize: 12 }}>
                [{officeText}]
              </Text>
            </span>
          ),
        };
      })
      .sort((a, b) => a.searchLabel.localeCompare(b.searchLabel));
  }, [simulateUsersQuery.data]);

  // Track if settings have been modified by user
  const [isModified, setIsModified] = useState(false);

  // Holds the full raw settingsJson from the server — used to preserve unknown keys on save and for display
  const rawSettingsJsonRef = useRef<Record<string, unknown>>({});
  const [rawSettingsJson, setRawSettingsJson] = useState<
    Record<string, unknown>
  >({});

  // GraphQL query to load settings
  const { loading: isLoading } = useQuery(GET_APP_SETTINGS_QUERY, {
    variables: { input: { settingsTab: SETTINGS_TAB } },
    onCompleted: (data) => {
      if (data?.getAppSettings?.settingsJson) {
        const fullJson = data.getAppSettings.settingsJson as Record<
          string,
          unknown
        >;
        rawSettingsJsonRef.current = fullJson;
        setRawSettingsJson(fullJson);
        const saved = data.getAppSettings
          .settingsJson as IRecruiterDashboardSettingsJson;
        // Apply loaded settings to state
        if (saved.showDebug !== undefined) setShowDebug(saved.showDebug);
        if (saved.excludedDates) {
          setExcludedDates(
            saved.excludedDates.map((d: string) =>
              parse(d, 'MM-dd', new Date(LEAP_YEAR, 0, 1))
            )
          );
        }
        if (saved.workingDays) setWorkingDays(saved.workingDays);
        if (saved.dayPrbcGoal !== undefined) setDayPrbcGoal(saved.dayPrbcGoal);
        if (saved.statusBreakpoints)
          setStatusBreakpoints(saved.statusBreakpoints);
        if (saved.statusLabels) setStatusLabels(saved.statusLabels);
        if (saved.roleRestrictionsEnabled !== undefined)
          setRoleRestrictionsEnabled(saved.roleRestrictionsEnabled);
        if (saved.roleRestrictions) setRoleRestrictions(saved.roleRestrictions);
        if (saved.activeDonorStatuses)
          setActiveDonorStatuses(saved.activeDonorStatuses);
        if (saved.pendingDonorStatuses)
          setPendingDonorStatuses(saved.pendingDonorStatuses);
        if (saved.approvedDonorStatuses)
          setApprovedDonorStatuses(saved.approvedDonorStatuses);
        if (saved.repeatDonationsThreshold !== undefined)
          setRepeatDonationsThreshold(saved.repeatDonationsThreshold);
        if (saved.churnRateBreakpoints)
          setChurnRateBreakpoints(saved.churnRateBreakpoints);
        if (saved.churnRateLabels) setChurnRateLabels(saved.churnRateLabels);
        if (saved.pendingDaysBreakpoints)
          setPendingDaysBreakpoints(saved.pendingDaysBreakpoints);
        if (saved.pendingDaysLabels)
          setPendingDaysLabels(saved.pendingDaysLabels);
        if (saved.donorDonationFrequency !== undefined)
          setDonorDonationFrequency(saved.donorDonationFrequency);
        if (saved.donorShowRateBreakpoints)
          setDonorShowRateBreakpoints(saved.donorShowRateBreakpoints);
        if (saved.donorShowRateLabels)
          setDonorShowRateLabels(saved.donorShowRateLabels);
        if (saved.lapsedDonorBreakpoints)
          setLapsedDonorBreakpoints(saved.lapsedDonorBreakpoints);
        if (saved.lapsedDonorLabels)
          setLapsedDonorLabels(saved.lapsedDonorLabels);
        if (saved.retiredDonorBreakpoints)
          setRetiredDonorBreakpoints(saved.retiredDonorBreakpoints);
        if (saved.retiredDonorLabels)
          setRetiredDonorLabels(saved.retiredDonorLabels);
        if (saved.historyDataCutoff)
          setHistoryDataCutoff(saved.historyDataCutoff);
        if (saved.activeLocationThresholdDays !== undefined)
          setActiveLocationThresholdDays(saved.activeLocationThresholdDays);
        if (saved.donorEligibilityDays !== undefined)
          setDonorEligibilityDays(saved.donorEligibilityDays);
        if (saved.ffpSglProductIds)
          setFfpSglProductIdsStr(saved.ffpSglProductIds.join(','));
        if (saved.ffpDblProductIds)
          setFfpDblProductIdsStr(saved.ffpDblProductIds.join(','));
        if (saved.avatars) setAvatars(saved.avatars);
        if (saved.avatarNames) setAvatarNames(saved.avatarNames);
        if (saved.defaultAvatarBase64 !== undefined)
          setDefaultAvatarBase64(saved.defaultAvatarBase64);
        if (saved.simulateRole !== undefined)
          setSimulateRole(saved.simulateRole);
        if (saved.simulateUserId !== undefined)
          setSimulateUserId(saved.simulateUserId);
      }
    },
  });

  // GraphQL mutation to save settings
  const [saveAppSettings, { loading: isSaving }] = useMutation(
    SAVE_APP_SETTINGS_MUTATION,
    {
      onCompleted: () => {
        message.success('Settings saved successfully');
      },
      onError: (error) => {
        message.error(`Failed to save settings: ${error.message}`);
      },
    }
  );

  // Build current settings JSON
  const currentSettings: IRecruiterDashboardSettingsJson = useMemo(
    () => ({
      showDebug,
      excludedDates: excludedDates.map((d) => format(d, 'MM-dd')),
      workingDays,
      dayPrbcGoal,
      statusBreakpoints,
      statusLabels,
      roleRestrictionsEnabled,
      roleRestrictions,
      activeDonorStatuses,
      pendingDonorStatuses,
      approvedDonorStatuses,
      repeatDonationsThreshold,
      churnRateBreakpoints,
      churnRateLabels,
      pendingDaysBreakpoints,
      pendingDaysLabels,
      donorDonationFrequency,
      donorShowRateBreakpoints,
      donorShowRateLabels,
      lapsedDonorBreakpoints,
      lapsedDonorLabels,
      retiredDonorBreakpoints,
      retiredDonorLabels,
      historyDataCutoff,
      activeLocationThresholdDays,
      donorEligibilityDays,
      ffpSglProductIds: ffpSglProductIdsStr
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter((n) => !isNaN(n)),
      ffpDblProductIds: ffpDblProductIdsStr
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter((n) => !isNaN(n)),
      avatars,
      avatarNames,
      defaultAvatarBase64,
      simulateRole,
      simulateUserId,
    }),
    [
      showDebug,
      excludedDates,
      workingDays,
      dayPrbcGoal,
      statusBreakpoints,
      statusLabels,
      roleRestrictionsEnabled,
      roleRestrictions,
      activeDonorStatuses,
      pendingDonorStatuses,
      approvedDonorStatuses,
      repeatDonationsThreshold,
      churnRateBreakpoints,
      churnRateLabels,
      pendingDaysBreakpoints,
      pendingDaysLabels,
      donorDonationFrequency,
      donorShowRateBreakpoints,
      donorShowRateLabels,
      lapsedDonorBreakpoints,
      lapsedDonorLabels,
      retiredDonorBreakpoints,
      retiredDonorLabels,
      historyDataCutoff,
      activeLocationThresholdDays,
      donorEligibilityDays,
      ffpSglProductIdsStr,
      ffpDblProductIdsStr,
      avatars,
      avatarNames,
      defaultAvatarBase64,
      simulateRole,
      simulateUserId,
    ]
  );

  // Check if there are unsaved changes
  const hasUnsavedChanges = isModified;

  // Handle save
  const handleSave = async () => {
    try {
      await saveAppSettings({
        variables: {
          input: {
            settingsTab: SETTINGS_TAB,
            settingsJson: {
              ...rawSettingsJsonRef.current,
              ...currentSettings,
            },
          },
        },
      });
      setIsModified(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  // Mark as modified when any setting changes
  const markModified = () => setIsModified(true);

  const toggleWorkingDay = (dayIndex: number) => {
    markModified();
    if (workingDays.includes(dayIndex)) {
      setWorkingDays(workingDays.filter((d) => d !== dayIndex));
    } else {
      setWorkingDays([...workingDays, dayIndex].sort());
    }
  };

  // Helper to compare dates by month and day only
  const isSameMonthDay = (a: Date, b: Date) =>
    a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const handleDateToggle = (date: Date) => {
    markModified();
    const isAlreadyExcluded = excludedDates.some((d) =>
      isSameMonthDay(d, date)
    );

    if (isAlreadyExcluded) {
      setExcludedDates(excludedDates.filter((d) => !isSameMonthDay(d, date)));
    } else {
      // Store with leap year to preserve Feb 29
      const normalizedDate = new Date(
        LEAP_YEAR,
        date.getMonth(),
        date.getDate()
      );
      setExcludedDates(
        [...excludedDates, normalizedDate].sort(
          (a, b) => a.getMonth() - b.getMonth() || a.getDate() - b.getDate()
        )
      );
    }
  };

  const handleRemoveDate = (dateToRemove: Date) => {
    markModified();
    setExcludedDates(
      excludedDates.filter((d) => !isSameMonthDay(d, dateToRemove))
    );
  };

  const handleClearAll = () => {
    markModified();
    setExcludedDates([]);
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card>
          <Title level={5}>{t('debugTitle', 'Debug Settings')}</Title>
          <Text type="secondary">
            {t(
              'debugDescription',
              'Show debug information on the recruiter dashboard.'
            )}
          </Text>

          <div style={{ marginTop: 16 }}>
            <Checkbox
              checked={showDebug}
              onChange={(e) => {
                markModified();
                setShowDebug(e.target.checked);
              }}
            >
              {t('showDebugLabel', 'Show Debug')}
            </Checkbox>
          </div>
        </Card>
      </Col>

      {/* Admin Simulate User Role */}
      <Col span={24}>
        <Card>
          <Title level={5}>Admin Simulate User Role</Title>
          <Text type="secondary">
            Simulate viewing this dashboard as a different user role. When a
            non-Admin role is selected, pick a specific user to impersonate
            their context (office, region, etc.). This is for testing role-based
            restrictions.
          </Text>

          <div style={{ marginTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Simulate Role
            </Text>
            <Select
              value={simulateRole}
              onChange={(value: UserRole | null) => {
                markModified();
                setSimulateRole(value ?? null);
                setSimulateUserId(null);
              }}
              style={{ width: '100%' }}
              allowClear
              placeholder="No simulation (Admin)"
            >
              {ALL_USER_ROLES.map((role) => (
                <Select.Option key={role} value={role}>
                  {role}
                </Select.Option>
              ))}
            </Select>
          </div>

          {simulateRole && simulateRole !== UserRole.Admin && (
            <div style={{ marginTop: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                Simulate User
              </Text>
              <Text
                type="secondary"
                style={{ display: 'block', fontSize: 12, marginBottom: 8 }}
              >
                Pick a specific user with the {simulateRole} role to impersonate
                their context (office, region, etc.)
              </Text>
              <Select
                value={simulateUserId}
                onChange={(value: string) => {
                  markModified();
                  setSimulateUserId(value ?? null);
                }}
                style={{ width: '100%' }}
                allowClear
                showSearch
                loading={simulateUsersQuery.loading}
                placeholder="Select a user to simulate..."
                filterOption={(input, option) =>
                  ((option as Record<string, unknown>)?.searchLabel as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase()) ?? false
                }
                options={simulateUserOptions}
              />
            </div>
          )}
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Title level={5}>{t('workingDaysTitle')}</Title>
          <Text type="secondary">{t('workingDaysDescription')}</Text>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              {!calendarOpen ? (
                <Button
                  icon={<CalendarOutlined />}
                  onClick={() => setCalendarOpen(true)}
                >
                  {t('showCalendar')}
                </Button>
              ) : (
                <Card
                  size="small"
                  title={t('excludeDatesLabel')}
                  extra={
                    <Button
                      type="text"
                      icon={<CloseOutlined />}
                      onClick={() => setCalendarOpen(false)}
                      size="small"
                    />
                  }
                  styles={{ body: { padding: 8 } }}
                >
                  <MultiDateCalendar
                    selectedDates={excludedDates}
                    onDateToggle={handleDateToggle}
                  />
                </Card>
              )}
            </Col>

            <Col xs={24} md={12}>
              <Row justify="space-between" align="middle">
                <Text strong>{t('selectedDatesLabel')}</Text>
                {excludedDates.length > 0 && (
                  <Button type="link" size="small" onClick={handleClearAll}>
                    {t('clearAll')}
                  </Button>
                )}
              </Row>
              <div style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
                {excludedDates.length === 0 ? (
                  <Text type="secondary">{t('noDatesSelected')}</Text>
                ) : (
                  <Space wrap>
                    {excludedDates.map((date) => (
                      <Tag
                        key={date.toISOString()}
                        closable
                        onClose={() => handleRemoveDate(date)}
                        color="red"
                      >
                        {format(date, 'MMM d')}
                      </Tag>
                    ))}
                  </Space>
                )}
              </div>
            </Col>
          </Row>

          <div style={{ marginTop: 24 }}>
            <Text type="secondary">{t('daysOfWeekDescription')}</Text>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              {WEEKDAYS_ISO.map(({ day, isoDay }) => {
                const isSelected = workingDays.includes(isoDay);
                return (
                  <div
                    key={isoDay}
                    onClick={() => toggleWorkingDay(isoDay)}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#1890ff' : '#f0f0f0',
                      color: isSelected ? '#fff' : '#666',
                      fontWeight: 500,
                      fontSize: 12,
                      transition: 'all 0.2s',
                      userSelect: 'none',
                    }}
                  >
                    {day}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Title level={5}>{t('monthlyGoalTitle')}</Title>
          <Text type="secondary">{t('monthlyGoalDescription')}</Text>

          <div style={{ marginTop: 16 }}>
            <Space>
              <Text>{t('dayPrbcGoalLabel')}:</Text>
              <InputNumber
                value={dayPrbcGoal}
                onChange={(value) => {
                  markModified();
                  setDayPrbcGoal(value);
                }}
                min={0}
                precision={0}
                style={{ width: 60 }}
                placeholder="0"
              />
            </Space>
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Title level={5}>FFP Product Classification</Title>
          <Text type="secondary">
            WooCommerce product IDs that determine SGL vs DBL for FFP units. DBL
            products count as 2 units in the FFP Collected total.
          </Text>

          <div style={{ marginTop: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text style={{ fontSize: 12 }}>FFP (SGL) Product IDs:</Text>
                <Input
                  value={ffpSglProductIdsStr}
                  onChange={(e) => {
                    markModified();
                    setFfpSglProductIdsStr(e.target.value.replace(/\s/g, ''));
                  }}
                  placeholder="502,8179"
                  style={{ width: 200 }}
                />
              </div>
              <div>
                <Text style={{ fontSize: 12 }}>FFP (DBL) Product IDs:</Text>
                <Input
                  value={ffpDblProductIdsStr}
                  onChange={(e) => {
                    markModified();
                    setFfpDblProductIdsStr(e.target.value.replace(/\s/g, ''));
                  }}
                  placeholder="503,504"
                  style={{ width: 200 }}
                />
              </div>
            </Space>
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Title level={5}>Repeat Donations Threshold</Title>
          <Text type="secondary">
            Minimum number of donations for a donor to be considered a repeat
            donor on the Recruiter Dashboard.
          </Text>

          <div style={{ marginTop: 16 }}>
            <Space>
              <Text>Threshold:</Text>
              <InputNumber
                value={repeatDonationsThreshold}
                onChange={(value) => {
                  markModified();
                  setRepeatDonationsThreshold(value ?? 4);
                }}
                min={2}
                max={8}
                precision={0}
                style={{ width: 60 }}
              />
            </Space>
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Title level={5}>Churn Rate Labels</Title>
          <Text type="secondary">
            Configure the status labels and thresholds for donor churn rate
            cards. Negative churn (net growth) is always &quot;
            {churnRateLabels.low}&quot;.
          </Text>

          <div style={{ marginTop: 24 }}>
            <Text strong>Churn Rate Breakpoints (%)</Text>

            <div style={{ marginTop: 16, padding: '0 8px' }}>
              {/* Colored bar with handles — green (good) left, yellow mid, red (poor) right */}
              <div
                style={{
                  position: 'relative',
                  height: 16,
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    height: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  {/* Good segment (green) */}
                  <div
                    style={{
                      width: `${(churnRateBreakpoints[0] / 20) * 100}%`,
                      backgroundColor: '#389e0d',
                      height: '100%',
                    }}
                  />
                  {/* OK segment (yellow) */}
                  <div
                    style={{
                      width: `${
                        ((churnRateBreakpoints[1] - churnRateBreakpoints[0]) /
                          20) *
                        100
                      }%`,
                      backgroundColor: '#faad14',
                      height: '100%',
                    }}
                  />
                  {/* Poor segment (red) */}
                  <div
                    style={{
                      width: `${((20 - churnRateBreakpoints[1]) / 20) * 100}%`,
                      backgroundColor: '#f31a25',
                      height: '100%',
                    }}
                  />
                </div>

                {/* Handle 1 — between good and ok */}
                <div
                  className="breakpoint-handle"
                  style={{
                    position: 'absolute',
                    left: `${(churnRateBreakpoints[0] / 20) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 15,
                    height: 30,
                    borderRadius: 4,
                    backgroundColor: '#4f6d7a',
                    border: '2px solid #fff',
                    cursor: 'ew-resize',
                    zIndex: 2,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const startX = e.clientX;
                    const startValue = churnRateBreakpoints[0];
                    const container = e.currentTarget.parentElement;
                    if (!container) return;
                    const containerWidth = container.offsetWidth;

                    const onMouseMove = (moveEvent: MouseEvent) => {
                      const delta = moveEvent.clientX - startX;
                      const deltaPercent = (delta / containerWidth) * 20;
                      const newValue = Math.round(
                        Math.min(
                          Math.max(0, startValue + deltaPercent),
                          churnRateBreakpoints[1] - 1
                        )
                      );
                      markModified();
                      setChurnRateBreakpoints([
                        newValue,
                        churnRateBreakpoints[1],
                      ]);
                    };

                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };

                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }}
                />

                {/* Handle 2 — between ok and poor */}
                <div
                  className="breakpoint-handle"
                  style={{
                    position: 'absolute',
                    left: `${(churnRateBreakpoints[1] / 20) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 15,
                    height: 30,
                    borderRadius: 4,
                    backgroundColor: '#4f6d7a',
                    border: '2px solid #fff',
                    cursor: 'ew-resize',
                    zIndex: 2,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const startX = e.clientX;
                    const startValue = churnRateBreakpoints[1];
                    const container = e.currentTarget.parentElement;
                    if (!container) return;
                    const containerWidth = container.offsetWidth;

                    const onMouseMove = (moveEvent: MouseEvent) => {
                      const delta = moveEvent.clientX - startX;
                      const deltaPercent = (delta / containerWidth) * 20;
                      const newValue = Math.round(
                        Math.min(
                          Math.max(
                            churnRateBreakpoints[0] + 1,
                            startValue + deltaPercent
                          ),
                          20
                        )
                      );
                      markModified();
                      setChurnRateBreakpoints([
                        churnRateBreakpoints[0],
                        newValue,
                      ]);
                    };

                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };

                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }}
                />
              </div>

              {/* Labels below the bar */}
              <div
                style={{
                  display: 'flex',
                  position: 'relative',
                  height: 50,
                  marginTop: 8,
                }}
              >
                {/* Good label (green) */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    width: `${(churnRateBreakpoints[0] / 20) * 100}%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    0 - {churnRateBreakpoints[0]}%
                  </Text>
                  <Input
                    value={churnRateLabels.low}
                    onChange={(e) => {
                      markModified();
                      setChurnRateLabels({
                        ...churnRateLabels,
                        low: e.target.value,
                      });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 10, marginTop: 4 }}
                  >
                    Low Churn Label
                  </Text>
                </div>

                {/* OK label (yellow) */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${(churnRateBreakpoints[0] / 20) * 100}%`,
                    width: `${
                      ((churnRateBreakpoints[1] - churnRateBreakpoints[0]) /
                        20) *
                      100
                    }%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {churnRateBreakpoints[0]} - {churnRateBreakpoints[1]}%
                  </Text>
                  <Input
                    value={churnRateLabels.mid}
                    onChange={(e) => {
                      markModified();
                      setChurnRateLabels({
                        ...churnRateLabels,
                        mid: e.target.value,
                      });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 10, marginTop: 4 }}
                  >
                    Mid Churn Label
                  </Text>
                </div>

                {/* Poor label (red) */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${(churnRateBreakpoints[1] / 20) * 100}%`,
                    width: `${((20 - churnRateBreakpoints[1]) / 20) * 100}%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {churnRateBreakpoints[1]} - 20%
                  </Text>
                  <Input
                    value={churnRateLabels.high}
                    onChange={(e) => {
                      markModified();
                      setChurnRateLabels({
                        ...churnRateLabels,
                        high: e.target.value,
                      });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 10, marginTop: 4 }}
                  >
                    High Churn Label
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Title level={5}>Pending Days Breakpoints</Title>
          <Text type="secondary">
            Configure the status labels and day thresholds for the Pending
            Donors table. Donors in pending status will be color-coded by how
            many days they&apos;ve been pending.
          </Text>

          <div style={{ marginTop: 24 }}>
            <Text strong>Pending Days Breakpoints (days)</Text>

            <div style={{ marginTop: 16, padding: '0 8px' }}>
              {/* Colored bar with handles — green (on track) left, yellow (approaching) mid, red (overdue) right */}
              <div
                style={{
                  position: 'relative',
                  height: 16,
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    height: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  {/* On Track segment (green) */}
                  <div
                    style={{
                      width: `${
                        ((pendingDaysBreakpoints[0] - 15) / 85) * 100
                      }%`,
                      backgroundColor: '#389e0d',
                      height: '100%',
                    }}
                  />
                  {/* Approaching segment (yellow) */}
                  <div
                    style={{
                      width: `${
                        ((pendingDaysBreakpoints[1] -
                          pendingDaysBreakpoints[0]) /
                          85) *
                        100
                      }%`,
                      backgroundColor: '#faad14',
                      height: '100%',
                    }}
                  />
                  {/* Overdue segment (red) */}
                  <div
                    style={{
                      width: `${
                        ((100 - pendingDaysBreakpoints[1]) / 85) * 100
                      }%`,
                      backgroundColor: '#f31a25',
                      height: '100%',
                    }}
                  />
                </div>

                {/* Handle 1 — between on track and approaching */}
                <div
                  className="breakpoint-handle"
                  style={{
                    position: 'absolute',
                    left: `${((pendingDaysBreakpoints[0] - 15) / 85) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 15,
                    height: 30,
                    borderRadius: 4,
                    backgroundColor: '#4f6d7a',
                    border: '2px solid #fff',
                    cursor: 'ew-resize',
                    zIndex: 2,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const startX = e.clientX;
                    const startValue = pendingDaysBreakpoints[0];
                    const container = e.currentTarget.parentElement;
                    if (!container) return;
                    const containerWidth = container.offsetWidth;

                    const onMouseMove = (moveEvent: MouseEvent) => {
                      const delta = moveEvent.clientX - startX;
                      const deltaPercent = (delta / containerWidth) * 85;
                      const newValue = Math.round(
                        Math.min(
                          Math.max(15, startValue + deltaPercent),
                          pendingDaysBreakpoints[1] - 1
                        )
                      );
                      markModified();
                      setPendingDaysBreakpoints([
                        newValue,
                        pendingDaysBreakpoints[1],
                      ]);
                    };

                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };

                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }}
                />

                {/* Handle 2 — between approaching and overdue */}
                <div
                  className="breakpoint-handle"
                  style={{
                    position: 'absolute',
                    left: `${((pendingDaysBreakpoints[1] - 15) / 85) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 15,
                    height: 30,
                    borderRadius: 4,
                    backgroundColor: '#4f6d7a',
                    border: '2px solid #fff',
                    cursor: 'ew-resize',
                    zIndex: 2,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const startX = e.clientX;
                    const startValue = pendingDaysBreakpoints[1];
                    const container = e.currentTarget.parentElement;
                    if (!container) return;
                    const containerWidth = container.offsetWidth;

                    const onMouseMove = (moveEvent: MouseEvent) => {
                      const delta = moveEvent.clientX - startX;
                      const deltaPercent = (delta / containerWidth) * 85;
                      const newValue = Math.round(
                        Math.min(
                          Math.max(
                            pendingDaysBreakpoints[0] + 1,
                            startValue + deltaPercent
                          ),
                          100
                        )
                      );
                      markModified();
                      setPendingDaysBreakpoints([
                        pendingDaysBreakpoints[0],
                        newValue,
                      ]);
                    };

                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };

                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }}
                />
              </div>

              {/* Labels below the bar */}
              <div
                style={{
                  display: 'flex',
                  position: 'relative',
                  height: 50,
                  marginTop: 8,
                }}
              >
                {/* On Track label (green) */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    width: `${((pendingDaysBreakpoints[0] - 15) / 85) * 100}%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    0 - {pendingDaysBreakpoints[0]} days
                  </Text>
                  <Input
                    value={pendingDaysLabels.low}
                    onChange={(e) => {
                      markModified();
                      setPendingDaysLabels({
                        ...pendingDaysLabels,
                        low: e.target.value,
                      });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                </div>

                {/* Approaching label (yellow) */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${((pendingDaysBreakpoints[0] - 15) / 85) * 100}%`,
                    width: `${
                      ((pendingDaysBreakpoints[1] - pendingDaysBreakpoints[0]) /
                        85) *
                      100
                    }%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {pendingDaysBreakpoints[0]} - {pendingDaysBreakpoints[1]}{' '}
                    days
                  </Text>
                  <Input
                    value={pendingDaysLabels.mid}
                    onChange={(e) => {
                      markModified();
                      setPendingDaysLabels({
                        ...pendingDaysLabels,
                        mid: e.target.value,
                      });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                </div>

                {/* Overdue label (red) */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${((pendingDaysBreakpoints[1] - 15) / 85) * 100}%`,
                    width: `${((100 - pendingDaysBreakpoints[1]) / 85) * 100}%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {pendingDaysBreakpoints[1]} - 100 days
                  </Text>
                  <Input
                    value={pendingDaysLabels.high}
                    onChange={(e) => {
                      markModified();
                      setPendingDaysLabels({
                        ...pendingDaysLabels,
                        high: e.target.value,
                      });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Title level={5}>Donor Show Rate</Title>
          <Text type="secondary">
            Configure the expected donation frequency and status labels for the
            Donor Show Rate card.
          </Text>

          <div style={{ marginTop: 16 }}>
            <Space>
              <Text>Donation Frequency (days):</Text>
              <InputNumber
                value={donorDonationFrequency}
                onChange={(value) => {
                  markModified();
                  setDonorDonationFrequency(value ?? 56);
                }}
                min={7}
                max={365}
                precision={0}
                style={{ width: 70 }}
              />
            </Space>
          </div>

          <div style={{ marginTop: 16 }}>
            <Space>
              <Text>Donor Eligibility (days after registration):</Text>
              <InputNumber
                value={donorEligibilityDays}
                onChange={(value) => {
                  markModified();
                  setDonorEligibilityDays(value ?? 40);
                }}
                min={1}
                max={365}
                precision={0}
                style={{ width: 70 }}
              />
            </Space>
          </div>

          <div style={{ marginTop: 24 }}>
            <Text strong>Show Rate Breakpoints (%)</Text>

            <div style={{ marginTop: 16, padding: '0 8px' }}>
              {/* Colored bar — red (poor) left, yellow mid, green (good) right */}
              <div
                style={{
                  position: 'relative',
                  height: 16,
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    height: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  {/* Poor segment (red) */}
                  <div
                    style={{
                      width: `${donorShowRateBreakpoints[0]}%`,
                      backgroundColor: '#f31a25',
                      height: '100%',
                    }}
                  />
                  {/* OK segment (yellow) */}
                  <div
                    style={{
                      width: `${
                        donorShowRateBreakpoints[1] -
                        donorShowRateBreakpoints[0]
                      }%`,
                      backgroundColor: '#faad14',
                      height: '100%',
                    }}
                  />
                  {/* Good segment (green) */}
                  <div
                    style={{
                      width: `${100 - donorShowRateBreakpoints[1]}%`,
                      backgroundColor: '#389e0d',
                      height: '100%',
                    }}
                  />
                </div>

                {/* Handle 1 — between poor and ok */}
                <div
                  className="breakpoint-handle"
                  style={{
                    position: 'absolute',
                    left: `${donorShowRateBreakpoints[0]}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 15,
                    height: 30,
                    borderRadius: 4,
                    backgroundColor: '#4f6d7a',
                    border: '2px solid #fff',
                    cursor: 'ew-resize',
                    zIndex: 2,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const startX = e.clientX;
                    const startValue = donorShowRateBreakpoints[0];
                    const container = e.currentTarget.parentElement;
                    if (!container) return;
                    const containerWidth = container.offsetWidth;

                    const onMouseMove = (moveEvent: MouseEvent) => {
                      const delta = moveEvent.clientX - startX;
                      const deltaPercent = (delta / containerWidth) * 100;
                      const newValue = Math.round(
                        Math.min(
                          Math.max(0, startValue + deltaPercent),
                          donorShowRateBreakpoints[1] - 1
                        )
                      );
                      markModified();
                      setDonorShowRateBreakpoints([
                        newValue,
                        donorShowRateBreakpoints[1],
                      ]);
                    };

                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };

                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }}
                />

                {/* Handle 2 — between ok and good */}
                <div
                  className="breakpoint-handle"
                  style={{
                    position: 'absolute',
                    left: `${donorShowRateBreakpoints[1]}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 15,
                    height: 30,
                    borderRadius: 4,
                    backgroundColor: '#4f6d7a',
                    border: '2px solid #fff',
                    cursor: 'ew-resize',
                    zIndex: 2,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const startX = e.clientX;
                    const startValue = donorShowRateBreakpoints[1];
                    const container = e.currentTarget.parentElement;
                    if (!container) return;
                    const containerWidth = container.offsetWidth;

                    const onMouseMove = (moveEvent: MouseEvent) => {
                      const delta = moveEvent.clientX - startX;
                      const deltaPercent = (delta / containerWidth) * 100;
                      const newValue = Math.round(
                        Math.min(
                          Math.max(
                            donorShowRateBreakpoints[0] + 1,
                            startValue + deltaPercent
                          ),
                          100
                        )
                      );
                      markModified();
                      setDonorShowRateBreakpoints([
                        donorShowRateBreakpoints[0],
                        newValue,
                      ]);
                    };

                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };

                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }}
                />
              </div>

              {/* Labels below the bar */}
              <div
                style={{
                  display: 'flex',
                  position: 'relative',
                  height: 50,
                  marginTop: 8,
                }}
              >
                {/* Poor label (red) */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    width: `${donorShowRateBreakpoints[0]}%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    0 - {donorShowRateBreakpoints[0]}%
                  </Text>
                  <Input
                    value={donorShowRateLabels.low}
                    onChange={(e) => {
                      markModified();
                      setDonorShowRateLabels({
                        ...donorShowRateLabels,
                        low: e.target.value,
                      });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                </div>

                {/* OK label (yellow) */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${donorShowRateBreakpoints[0]}%`,
                    width: `${
                      donorShowRateBreakpoints[1] - donorShowRateBreakpoints[0]
                    }%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {donorShowRateBreakpoints[0]} -{' '}
                    {donorShowRateBreakpoints[1]}%
                  </Text>
                  <Input
                    value={donorShowRateLabels.mid}
                    onChange={(e) => {
                      markModified();
                      setDonorShowRateLabels({
                        ...donorShowRateLabels,
                        mid: e.target.value,
                      });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                </div>

                {/* Good label (green) */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${donorShowRateBreakpoints[1]}%`,
                    width: `${100 - donorShowRateBreakpoints[1]}%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {donorShowRateBreakpoints[1]} - 100%
                  </Text>
                  <Input
                    value={donorShowRateLabels.high}
                    onChange={(e) => {
                      markModified();
                      setDonorShowRateLabels({
                        ...donorShowRateLabels,
                        high: e.target.value,
                      });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Title level={5}>Lapsed Donors Breakpoints</Title>
          <Text type="secondary">
            Configure the day breakpoints for lapsed donor ranges. Donors are
            grouped by how many days since their last donation. Minimum 2
            breakpoints required.
          </Text>

          <div style={{ marginTop: 24 }}>
            <Text strong>Lapsed Day Breakpoints</Text>

            {(() => {
              const LAPSED_COLORS = [
                '#389e0d',
                '#faad14',
                '#fa8c16',
                '#f5222d',
              ];
              const getLapsedColor = (idx: number) =>
                idx < LAPSED_COLORS.length
                  ? LAPSED_COLORS[idx]
                  : LAPSED_COLORS[LAPSED_COLORS.length - 1];
              const maxScale =
                lapsedDonorBreakpoints[lapsedDonorBreakpoints.length - 1] + 30;

              return (
                <div style={{ marginTop: 16, padding: '0 8px' }}>
                  {/* Colored bar with draggable handles */}
                  <div
                    style={{
                      position: 'relative',
                      height: 16,
                      marginTop: 10,
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        height: '100%',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      {[...lapsedDonorBreakpoints, maxScale].map((bp, idx) => {
                        const prevBp =
                          idx === 0 ? 0 : lapsedDonorBreakpoints[idx - 1];
                        return (
                          <div
                            key={idx}
                            style={{
                              width: `${((bp - prevBp) / maxScale) * 100}%`,
                              backgroundColor: getLapsedColor(idx),
                              height: '100%',
                            }}
                          />
                        );
                      })}
                    </div>

                    {/* Draggable handles */}
                    {lapsedDonorBreakpoints.map((bp, idx) => (
                      <div
                        key={idx}
                        className="breakpoint-handle"
                        style={{
                          position: 'absolute',
                          left: `${(bp / maxScale) * 100}%`,
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: 15,
                          height: 30,
                          borderRadius: 4,
                          backgroundColor: '#4f6d7a',
                          border: '2px solid #fff',
                          cursor: 'ew-resize',
                          zIndex: 2,
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const startX = e.clientX;
                          const startValue = bp;
                          const container = e.currentTarget.parentElement;
                          if (!container) return;
                          const containerWidth = container.offsetWidth;

                          const onMouseMove = (moveEvent: MouseEvent) => {
                            const delta = moveEvent.clientX - startX;
                            const deltaValue =
                              (delta / containerWidth) * maxScale;
                            const minVal =
                              idx === 0
                                ? 1
                                : lapsedDonorBreakpoints[idx - 1] + 1;
                            const maxVal =
                              idx < lapsedDonorBreakpoints.length - 1
                                ? lapsedDonorBreakpoints[idx + 1] - 1
                                : maxScale - 1;
                            const newValue = Math.round(
                              Math.min(
                                Math.max(minVal, startValue + deltaValue),
                                maxVal
                              )
                            );
                            markModified();
                            const updated = [...lapsedDonorBreakpoints];
                            updated[idx] = newValue;
                            setLapsedDonorBreakpoints(updated);
                          };

                          const onMouseUp = () => {
                            document.removeEventListener(
                              'mousemove',
                              onMouseMove
                            );
                            document.removeEventListener('mouseup', onMouseUp);
                          };

                          document.addEventListener('mousemove', onMouseMove);
                          document.addEventListener('mouseup', onMouseUp);
                        }}
                      />
                    ))}
                  </div>

                  {/* Range labels below the bar */}
                  <div
                    style={{
                      display: 'flex',
                      position: 'relative',
                      height: 80,
                      marginTop: 8,
                    }}
                  >
                    {[...lapsedDonorBreakpoints, maxScale].map((bp, idx) => {
                      const prevBp =
                        idx === 0 ? 0 : lapsedDonorBreakpoints[idx - 1];
                      const isLast = idx === lapsedDonorBreakpoints.length;
                      const rangeLabel = isLast
                        ? `${prevBp + 1}+`
                        : `${prevBp === 0 ? 0 : prevBp + 1}-${bp}`;
                      return (
                        <div
                          key={idx}
                          style={{
                            position: 'absolute',
                            left: `${(prevBp / maxScale) * 100}%`,
                            width: `${((bp - prevBp) / maxScale) * 100}%`,
                            textAlign: 'center',
                          }}
                        >
                          <Text
                            style={{
                              display: 'block',
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {rangeLabel} days
                          </Text>
                          <Input
                            value={lapsedDonorLabels[idx] ?? ''}
                            onChange={(e) => {
                              markModified();
                              const updated = [...lapsedDonorLabels];
                              updated[idx] = e.target.value;
                              setLapsedDonorLabels(updated);
                            }}
                            size="small"
                            style={{
                              width: 90,
                              fontSize: 11,
                              textAlign: 'center',
                              padding: '2px 4px',
                              height: 24,
                              border: '2px solid #4f6d7a',
                            }}
                          />
                          {!isLast && lapsedDonorBreakpoints.length > 2 && (
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              style={{ fontSize: 10, height: 20, marginTop: 2 }}
                              onClick={() => {
                                markModified();
                                setLapsedDonorBreakpoints(
                                  lapsedDonorBreakpoints.filter(
                                    (_, i) => i !== idx
                                  )
                                );
                                // Remove the label for this range; the range after merges with this one
                                setLapsedDonorLabels(
                                  lapsedDonorLabels.filter((_, i) => i !== idx)
                                );
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Add breakpoint button */}
                  <Button
                    type="dashed"
                    size="small"
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      markModified();
                      const lastBp =
                        lapsedDonorBreakpoints[
                          lapsedDonorBreakpoints.length - 1
                        ];
                      // Insert new breakpoint before the last (overflow) range
                      setLapsedDonorBreakpoints([
                        ...lapsedDonorBreakpoints,
                        lastBp + 30,
                      ]);
                      // Insert a new label before the last label
                      const newLabels = [...lapsedDonorLabels];
                      newLabels.splice(newLabels.length - 1, 0, 'New Range');
                      setLapsedDonorLabels(newLabels);
                    }}
                  >
                    Add Breakpoint
                  </Button>
                </div>
              );
            })()}
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Title level={5}>Retired Donors Breakpoints</Title>
          <Text type="secondary">
            Configure the day breakpoints for retired donor (Unresponsive)
            ranges. Donors are grouped by how many days ago they transitioned to
            Retired status. Minimum 2 breakpoints required.
          </Text>

          <div style={{ marginTop: 24 }}>
            <Text strong>Retired Day Breakpoints</Text>

            {(() => {
              const RETIRED_COLORS = [
                '#389e0d',
                '#faad14',
                '#fa8c16',
                '#f5222d',
              ];
              const getRetiredColor = (idx: number) =>
                idx < RETIRED_COLORS.length
                  ? RETIRED_COLORS[idx]
                  : RETIRED_COLORS[RETIRED_COLORS.length - 1];
              const maxScale =
                retiredDonorBreakpoints[retiredDonorBreakpoints.length - 1] +
                30;

              return (
                <div style={{ marginTop: 16, padding: '0 8px' }}>
                  {/* Colored bar with draggable handles */}
                  <div
                    style={{
                      position: 'relative',
                      height: 16,
                      marginTop: 10,
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        height: '100%',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      {[...retiredDonorBreakpoints, maxScale].map((bp, idx) => {
                        const prevBp =
                          idx === 0 ? 0 : retiredDonorBreakpoints[idx - 1];
                        return (
                          <div
                            key={idx}
                            style={{
                              width: `${((bp - prevBp) / maxScale) * 100}%`,
                              backgroundColor: getRetiredColor(idx),
                              height: '100%',
                            }}
                          />
                        );
                      })}
                    </div>

                    {/* Draggable handles */}
                    {retiredDonorBreakpoints.map((bp, idx) => (
                      <div
                        key={idx}
                        className="breakpoint-handle"
                        style={{
                          position: 'absolute',
                          left: `${(bp / maxScale) * 100}%`,
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: 15,
                          height: 30,
                          borderRadius: 4,
                          backgroundColor: '#4f6d7a',
                          border: '2px solid #fff',
                          cursor: 'ew-resize',
                          zIndex: 2,
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const startX = e.clientX;
                          const startValue = bp;
                          const container = e.currentTarget.parentElement;
                          if (!container) return;
                          const containerWidth = container.offsetWidth;

                          const onMouseMove = (moveEvent: MouseEvent) => {
                            const delta = moveEvent.clientX - startX;
                            const deltaValue =
                              (delta / containerWidth) * maxScale;
                            const minVal =
                              idx === 0
                                ? 1
                                : retiredDonorBreakpoints[idx - 1] + 1;
                            const maxVal =
                              idx < retiredDonorBreakpoints.length - 1
                                ? retiredDonorBreakpoints[idx + 1] - 1
                                : maxScale - 1;
                            const newValue = Math.round(
                              Math.min(
                                Math.max(minVal, startValue + deltaValue),
                                maxVal
                              )
                            );
                            markModified();
                            const updated = [...retiredDonorBreakpoints];
                            updated[idx] = newValue;
                            setRetiredDonorBreakpoints(updated);
                          };

                          const onMouseUp = () => {
                            document.removeEventListener(
                              'mousemove',
                              onMouseMove
                            );
                            document.removeEventListener('mouseup', onMouseUp);
                          };

                          document.addEventListener('mousemove', onMouseMove);
                          document.addEventListener('mouseup', onMouseUp);
                        }}
                      />
                    ))}
                  </div>

                  {/* Range labels below the bar */}
                  <div
                    style={{
                      display: 'flex',
                      position: 'relative',
                      height: 80,
                      marginTop: 8,
                    }}
                  >
                    {[...retiredDonorBreakpoints, maxScale].map((bp, idx) => {
                      const prevBp =
                        idx === 0 ? 0 : retiredDonorBreakpoints[idx - 1];
                      const isLast = idx === retiredDonorBreakpoints.length;
                      const rangeLabel = isLast
                        ? `${prevBp + 1}+`
                        : `${prevBp === 0 ? 0 : prevBp + 1}-${bp}`;
                      return (
                        <div
                          key={idx}
                          style={{
                            position: 'absolute',
                            left: `${(prevBp / maxScale) * 100}%`,
                            width: `${((bp - prevBp) / maxScale) * 100}%`,
                            textAlign: 'center',
                          }}
                        >
                          <Text
                            style={{
                              display: 'block',
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {rangeLabel} days
                          </Text>
                          <Input
                            value={retiredDonorLabels[idx] ?? ''}
                            onChange={(e) => {
                              markModified();
                              const updated = [...retiredDonorLabels];
                              updated[idx] = e.target.value;
                              setRetiredDonorLabels(updated);
                            }}
                            size="small"
                            style={{
                              width: 90,
                              fontSize: 11,
                              textAlign: 'center',
                              padding: '2px 4px',
                              height: 24,
                              border: '2px solid #4f6d7a',
                            }}
                          />
                          {!isLast && retiredDonorBreakpoints.length > 2 && (
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              style={{ fontSize: 10, height: 20, marginTop: 2 }}
                              onClick={() => {
                                markModified();
                                setRetiredDonorBreakpoints(
                                  retiredDonorBreakpoints.filter(
                                    (_, i) => i !== idx
                                  )
                                );
                                setRetiredDonorLabels(
                                  retiredDonorLabels.filter((_, i) => i !== idx)
                                );
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Add breakpoint button */}
                  <Button
                    type="dashed"
                    size="small"
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      markModified();
                      const lastBp =
                        retiredDonorBreakpoints[
                          retiredDonorBreakpoints.length - 1
                        ];
                      setRetiredDonorBreakpoints([
                        ...retiredDonorBreakpoints,
                        lastBp + 30,
                      ]);
                      const newLabels = [...retiredDonorLabels];
                      newLabels.splice(newLabels.length - 1, 0, 'New Range');
                      setRetiredDonorLabels(newLabels);
                    }}
                  >
                    Add Breakpoint
                  </Button>
                </div>
              );
            })()}
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Title level={5}>{t('quarterlyGoalTitle')}</Title>
          <Text type="secondary">{t('quarterlyGoalDescription')}</Text>

          <div style={{ marginTop: 24 }}>
            <Text strong>{t('statusBreakpointsLabel')}</Text>

            <div style={{ marginTop: 16, padding: '0 8px' }}>
              {/* Custom slider with colored bar and handles */}
              <div
                style={{
                  position: 'relative',
                  height: 16,
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                {/* Colored bar container */}
                <div
                  style={{
                    display: 'flex',
                    height: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  {/* Offtrack segment */}
                  <div
                    style={{
                      width: `${statusBreakpoints[0]}%`,
                      backgroundColor: STATUS_COLORS.offtrack,
                      height: '100%',
                    }}
                  />
                  {/* At Risk segment */}
                  <div
                    style={{
                      width: `${statusBreakpoints[1] - statusBreakpoints[0]}%`,
                      backgroundColor: STATUS_COLORS.atRisk,
                      height: '100%',
                    }}
                  />
                  {/* On Track segment */}
                  <div
                    style={{
                      width: `${100 - statusBreakpoints[1]}%`,
                      backgroundColor: STATUS_COLORS.onTrack,
                      height: '100%',
                    }}
                  />
                </div>

                {/* Handle 1 - between offtrack and at risk */}
                <div
                  className="breakpoint-handle"
                  style={{
                    position: 'absolute',
                    left: `${statusBreakpoints[0]}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 15,
                    height: 30,
                    borderRadius: 4,
                    backgroundColor: '#4f6d7a',
                    border: '2px solid #fff',
                    cursor: 'ew-resize',
                    zIndex: 2,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const startX = e.clientX;
                    const startValue = statusBreakpoints[0];
                    const container = e.currentTarget.parentElement;
                    if (!container) return;
                    const containerWidth = container.offsetWidth;

                    const onMouseMove = (moveEvent: MouseEvent) => {
                      const delta = moveEvent.clientX - startX;
                      const deltaPercent = (delta / containerWidth) * 100;
                      const newValue = Math.round(
                        Math.min(
                          Math.max(0, startValue + deltaPercent),
                          statusBreakpoints[1] - 1
                        )
                      );
                      markModified();
                      setStatusBreakpoints([newValue, statusBreakpoints[1]]);
                    };

                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };

                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }}
                />

                {/* Handle 2 - between at risk and on track */}
                <div
                  className="breakpoint-handle"
                  style={{
                    position: 'absolute',
                    left: `${statusBreakpoints[1]}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 15,
                    height: 30,
                    borderRadius: 4,
                    backgroundColor: '#4f6d7a',
                    border: '2px solid #fff',
                    cursor: 'ew-resize',
                    zIndex: 2,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const startX = e.clientX;
                    const startValue = statusBreakpoints[1];
                    const container = e.currentTarget.parentElement;
                    if (!container) return;
                    const containerWidth = container.offsetWidth;

                    const onMouseMove = (moveEvent: MouseEvent) => {
                      const delta = moveEvent.clientX - startX;
                      const deltaPercent = (delta / containerWidth) * 100;
                      const newValue = Math.round(
                        Math.min(
                          Math.max(
                            statusBreakpoints[0] + 1,
                            startValue + deltaPercent
                          ),
                          100
                        )
                      );
                      markModified();
                      setStatusBreakpoints([statusBreakpoints[0], newValue]);
                    };

                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };

                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }}
                />
              </div>

              {/* Labels below the bar */}
              <div
                style={{
                  display: 'flex',
                  position: 'relative',
                  height: 50,
                  marginTop: 8,
                }}
              >
                {/* Offtrack label */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    width: `${statusBreakpoints[0]}%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    0 - {statusBreakpoints[0]}%
                  </Text>
                  <Input
                    value={statusLabels.low}
                    onChange={(e) => {
                      markModified();
                      setStatusLabels({ ...statusLabels, low: e.target.value });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 10, marginTop: 4 }}
                  >
                    Set Low Status Label
                  </Text>
                </div>

                {/* At Risk label */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${statusBreakpoints[0]}%`,
                    width: `${statusBreakpoints[1] - statusBreakpoints[0]}%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {statusBreakpoints[0]} - {statusBreakpoints[1]}%
                  </Text>
                  <Input
                    value={statusLabels.mid}
                    onChange={(e) => {
                      markModified();
                      setStatusLabels({ ...statusLabels, mid: e.target.value });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 10, marginTop: 4 }}
                  >
                    Set Mid Status Label
                  </Text>
                </div>

                {/* On Track label */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${statusBreakpoints[1]}%`,
                    width: `${100 - statusBreakpoints[1]}%`,
                    textAlign: 'center',
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {statusBreakpoints[1]} - 100%
                  </Text>
                  <Input
                    value={statusLabels.high}
                    onChange={(e) => {
                      markModified();
                      setStatusLabels({
                        ...statusLabels,
                        high: e.target.value,
                      });
                    }}
                    size="small"
                    style={{
                      width: 100,
                      fontSize: 11,
                      textAlign: 'center',
                      padding: '2px 4px',
                      height: 24,
                      border: '2px solid #4f6d7a',
                    }}
                  />
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 10, marginTop: 4 }}
                  >
                    Set High Status Label
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Col>

      {/* Role-Based Filter Restrictions */}
      <Col span={24}>
        <Card>
          <Title level={5}>{t('roleRestrictionsTitle')}</Title>
          <Text type="secondary">{t('roleRestrictionsDescription')}</Text>

          <div style={{ marginTop: 16 }}>
            <Checkbox
              checked={roleRestrictionsEnabled}
              onChange={(e) => {
                markModified();
                setRoleRestrictionsEnabled(e.target.checked);
              }}
            >
              {t('roleRestrictionsEnabledLabel')}
            </Checkbox>
          </div>

          {roleRestrictionsEnabled && (
            <div style={{ marginTop: 24 }}>
              <Text strong style={{ display: 'block', marginBottom: 16 }}>
                {t('roleRestrictionsPerRoleLabel')}
              </Text>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 12,
                }}
              >
                {ALL_USER_ROLES.map((role) => {
                  const restriction = {
                    ...DEFAULT_ROLE_RESTRICTION,
                    ...roleRestrictions[role],
                  };
                  return (
                    <Card
                      key={role}
                      size="small"
                      style={{
                        backgroundColor: '#fafafa',
                        border: '1px solid #e8e8e8',
                      }}
                    >
                      <Text
                        strong
                        style={{ display: 'block', marginBottom: 8 }}
                      >
                        {role}
                      </Text>
                      <Space direction="vertical" size={4}>
                        <Checkbox
                          checked={restriction.hideDashboard}
                          onChange={(e) => {
                            markModified();
                            setRoleRestrictions({
                              ...roleRestrictions,
                              [role]: {
                                ...restriction,
                                hideDashboard: e.target.checked,
                              },
                            });
                          }}
                        >
                          {t('hideDashboardLabel')}
                        </Checkbox>
                        <Checkbox
                          checked={restriction.lockToRegion}
                          disabled={restriction.hideDashboard}
                          onChange={(e) => {
                            markModified();
                            setRoleRestrictions({
                              ...roleRestrictions,
                              [role]: {
                                ...restriction,
                                lockToRegion: e.target.checked,
                              },
                            });
                          }}
                        >
                          {t('lockToRegionLabel')}
                        </Checkbox>
                        <Checkbox
                          checked={restriction.lockToOffice}
                          disabled={restriction.hideDashboard}
                          onChange={(e) => {
                            markModified();
                            setRoleRestrictions({
                              ...roleRestrictions,
                              [role]: {
                                ...restriction,
                                lockToOffice: e.target.checked,
                              },
                            });
                          }}
                        >
                          {t('lockToOfficeLabel')}
                        </Checkbox>
                        <Checkbox
                          checked={restriction.hideRollingDates}
                          disabled={restriction.hideDashboard}
                          onChange={(e) => {
                            markModified();
                            setRoleRestrictions({
                              ...roleRestrictions,
                              [role]: {
                                ...restriction,
                                hideRollingDates: e.target.checked,
                              },
                            });
                          }}
                        >
                          Hide Rolling Date Filters
                        </Checkbox>
                        <Checkbox
                          checked={restriction.hideCalendarDates}
                          disabled={restriction.hideDashboard}
                          onChange={(e) => {
                            markModified();
                            setRoleRestrictions({
                              ...roleRestrictions,
                              [role]: {
                                ...restriction,
                                hideCalendarDates: e.target.checked,
                              },
                            });
                          }}
                        >
                          Hide Calendar Date Filters
                        </Checkbox>
                        <Checkbox
                          checked={
                            restriction.showLocationRecruitStats ??
                            role === UserRole.Admin
                          }
                          disabled={restriction.hideDashboard}
                          onChange={(e) => {
                            markModified();
                            setRoleRestrictions({
                              ...roleRestrictions,
                              [role]: {
                                ...restriction,
                                showLocationRecruitStats: e.target.checked,
                              },
                            });
                          }}
                        >
                          Show Location New Recruit Stats
                        </Checkbox>
                      </Space>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      </Col>

      {/* Active Donor Statuses for Pipeline Health */}
      <Col span={24}>
        <Card>
          <Title level={5}>{t('activeDonorStatusesTitle')}</Title>
          <Text type="secondary">{t('activeDonorStatusesDescription')}</Text>

          <div style={{ marginTop: 16 }}>
            <Checkbox.Group
              value={activeDonorStatuses}
              onChange={(values) => {
                markModified();
                setActiveDonorStatuses(values as DonorStatus[]);
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 8,
                }}
              >
                {ALL_DONOR_STATUSES.map((status) => (
                  <Checkbox key={status} value={status}>
                    {status}
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          </div>
        </Card>
      </Col>

      {/* Pending Donor Statuses */}
      <Col span={24}>
        <Card>
          <Title level={5}>{t('pendingDonorStatusesTitle')}</Title>
          <Text type="secondary">{t('pendingDonorStatusesDescription')}</Text>

          <div style={{ marginTop: 16 }}>
            <Checkbox.Group
              value={pendingDonorStatuses}
              onChange={(values) => {
                markModified();
                setPendingDonorStatuses(values as DonorStatus[]);
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 8,
                }}
              >
                {ALL_DONOR_STATUSES.map((status) => (
                  <Checkbox key={status} value={status}>
                    {status}
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          </div>
        </Card>
      </Col>

      {/* Approved Donor Statuses */}
      <Col span={24}>
        <Card>
          <Title level={5}>{t('approvedDonorStatusesTitle')}</Title>
          <Text type="secondary">{t('approvedDonorStatusesDescription')}</Text>

          <div style={{ marginTop: 16 }}>
            <Checkbox.Group
              value={approvedDonorStatuses}
              onChange={(values) => {
                markModified();
                setApprovedDonorStatuses(values as DonorStatus[]);
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 8,
                }}
              >
                {ALL_DONOR_STATUSES.map((status) => (
                  <Checkbox key={status} value={status}>
                    {status}
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          </div>
        </Card>
      </Col>

      {/* History Data Cutoff */}
      <Col span={24}>
        <Card>
          <Title level={5}>History Data Cutoff</Title>
          <Text type="secondary">
            Periods before this date are excluded from all history charts that
            rely on donor status change history. Set this to the date your
            donor_status_change_history tracking began to avoid misleading bars.
          </Text>
          <div style={{ marginTop: 16 }}>
            <Space>
              <Text>Cutoff Date:</Text>
              <DatePicker
                value={historyDataCutoff ? moment(historyDataCutoff) : null}
                onChange={(date) => {
                  markModified();
                  setHistoryDataCutoff(
                    date
                      ? date.format('YYYY-MM-DD')
                      : DEFAULT_SETTINGS.historyDataCutoff
                  );
                }}
                format="MM/DD/YYYY"
                allowClear={false}
                style={{ width: 150 }}
              />
            </Space>
          </div>
        </Card>
      </Col>

      {/* Active Location Threshold */}
      <Col span={24}>
        <Card>
          <Title level={5}>{t('activeLocationThresholdTitle')}</Title>
          <Text type="secondary">
            {t('activeLocationThresholdDescription')}
          </Text>
          <div style={{ marginTop: 16 }}>
            <Space>
              <Text>Days:</Text>
              <InputNumber
                min={1}
                max={365}
                value={activeLocationThresholdDays}
                onChange={(val) => {
                  markModified();
                  setActiveLocationThresholdDays(val ?? 90);
                }}
                style={{ width: 100 }}
              />
            </Space>
          </div>
        </Card>
      </Col>

      {/* Avatar Management */}
      <Col span={24}>
        <AvatarManagementSection
          avatars={avatars}
          setAvatars={setAvatars}
          avatarNames={avatarNames}
          setAvatarNames={setAvatarNames}
          defaultAvatarBase64={defaultAvatarBase64}
          setDefaultAvatarBase64={setDefaultAvatarBase64}
          markModified={markModified}
        />
      </Col>

      {/* Settings JSON Accordion and Save Button */}
      <Col span={24}>
        <Card>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={isSaving}
                disabled={!hasUnsavedChanges}
              >
                {t('saveSettings')}
              </Button>
              {hasUnsavedChanges && (
                <Text style={{ color: '#f31a25', fontWeight: 500 }}>
                  ({t('notSaved')})
                </Text>
              )}
            </Space>
          </Row>

          <Collapse>
            <Collapse.Panel
              key="settings-json"
              header={
                <Space>
                  <span>{t('settingsJsonLabel')}</span>
                  {hasUnsavedChanges && (
                    <span style={{ color: '#f31a25', fontSize: 12 }}>
                      ({t('notSaved')})
                    </span>
                  )}
                </Space>
              }
            >
              <pre
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  fontSize: 12,
                  margin: 0,
                }}
              >
                {JSON.stringify(
                  { ...rawSettingsJson, ...currentSettings },
                  null,
                  2
                )}
              </pre>
            </Collapse.Panel>
          </Collapse>
        </Card>
      </Col>
    </Row>
  );
};

export default RecruiterDashboardSettings;
